import type { GapModifierValue } from "schemas/generated/fileModifiers";

import React from "react";

import SvgGapModifier from "@svg/modifiers/gap-modifier";

import { LengthInputSelector } from "@v2/features/modifiers/components/LengthInputSelector";
import { useOptimisticModifierValue } from "@v2/features/modifiers/hooks/useOptimisticModifierValue";
import { ChevronDown } from "lucide-react";
import { parseUnit } from "replo-runtime/shared/utils/units";

import { GAP_MODIFIER_OPTIONS } from "../constants";

interface GapModifierProps {
  onChange: (modifiers: GapModifierValue[]) => void;
  disabled?: boolean;
}

const GapModifierComponent: React.FC<GapModifierProps> = ({
  onChange,
  disabled = false,
}) => {
  const currentValue = useOptimisticModifierValue({
    modifierType: { type: "containerGap", value: 0 },
    extractor: (elementDesignation) => {
      return elementDesignation.element.styleOverrides?.gap ?? null;
    },
  });
  const displayValue = currentValue != null ? `${currentValue}px` : "";

  const handleValueChange = (gap: number) => {
    onChange([
      {
        type: "containerGap" as const,
        value: gap,
      },
    ]);
  };

  return (
    <div className="container-gap-modifier">
      <LengthInputSelector
        field="gap"
        value={displayValue}
        startEnhancer={<SvgGapModifier className="h-4 w-4" />}
        onChange={(newValue) => {
          const result = parseUnit(
            newValue,
            { value: 0, unit: "px" },
            "default",
            "px",
          );
          const numericValue =
            typeof result.value === "number"
              ? result.value
              : Number.parseFloat(result.value);
          if (!isNaN(numericValue) && numericValue >= 0) {
            handleValueChange(numericValue);
          }
        }}
        placeholder="0px"
        metrics={["px"]}
        unitDefaults={{ value: "0", unit: "px" }}
        menuOptions={GAP_MODIFIER_OPTIONS.map((option) => ({
          ...option,
          value: option.value.toString(),
        }))}
        minValues={{ px: 0 }}
        inputClassName="w-full"
        endEnhancer={() => <ChevronDown size={12} />}
        isDisabled={disabled}
      />
    </div>
  );
};

export default GapModifierComponent;
